import React, { useEffect } from 'react'
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
} from '@surecompanies/core_components'
import styled from 'styled-components'
import {
  getPaymentAssignmentStatusLabel,
  PaymentCase,
  PaymentStatus,
} from '../Utils/types'
import { PaymentAssignmentRole } from '../Utils/roles'
import type { PaymentAssignmentRolePolicy } from '../Utils/rolePolicy'

const CaseInfo = styled.div`
  margin-bottom: 16px;
  font-size: 14px;
  color: #3a4951;
`

const CaseLink = styled.span`
  color: #003146;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #004d66;
  }
`

interface UpdateStatusModalProps {
  visible: boolean
  paymentCase: PaymentCase | null
  onClose: () => void
  onStatusUpdate: (
    caseId: number,
    newStatus: PaymentStatus,
    note: string
  ) => void
  currentPaymentAssignmentRole: PaymentAssignmentRole
  rolePolicy: PaymentAssignmentRolePolicy
}

export default function UpdateStatusModal({
  visible,
  paymentCase,
  onClose,
  onStatusUpdate,
  currentPaymentAssignmentRole: _currentPaymentAssignmentRole,
  rolePolicy,
}: UpdateStatusModalProps) {
  const [form] = Form.useForm()

  useEffect(() => {
    if (paymentCase) {
      form.setFieldsValue({
        status: paymentCase.paymentStatus?.id ?? undefined,
        note: '',
      })
    }
  }, [paymentCase, form])

  if (!paymentCase) return null

  const getAvailableStatuses = (): PaymentStatus[] =>
    rolePolicy.allowedStatuses as PaymentStatus[]

  const handleSubmit = () => {
    if (!rolePolicy.canUpdateStatus) {
      return
    }
    form
      .validateFields()
      .then((values) => {
        onStatusUpdate(paymentCase.id, values.status, values.note || '')
        form.resetFields()
        onClose()
      })
      .catch((errorInfo) => {
        // Validation failed - error is already shown by Ant Design form
        console.log('Validation failed:', errorInfo)
      })
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title="Update Status"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <CaseInfo>
        Case:{' '}
        <CaseLink>
          {paymentCase.benefit?.carrierName ?? '—'} / {paymentCase.benefitId}
        </CaseLink>
      </CaseInfo>

      <Form form={form} layout="vertical">
        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Please select a status' }]}
        >
          <Select placeholder="Select Status" labelInValue={false}>
            {getAvailableStatuses().map((status) => (
              <Select.Option key={status} value={status}>
                {getPaymentAssignmentStatusLabel(status)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="note" label="Comment / Note">
          <Input.TextArea
            rows={4}
            placeholder="Add a comment or note about this status update..."
            style={{ resize: 'vertical' }}
          />
        </Form.Item>
      </Form>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '24px',
        }}
      >
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={!rolePolicy.canUpdateStatus}
        >
          Update Status
        </Button>
      </div>
    </Modal>
  )
}
